import { useState } from "react";
import { Link } from "react-router-dom";

import { useAuth } from "../../context/AuthContext";
import { useChat } from "../../hooks/useChat";
import { useUserPreferences } from "../../hooks/useUserPreferences";
import { PersonaOnboarding } from "../onboarding/PersonaOnboarding";
import { PersonaAvatar } from "../persona/PersonaAvatar";
import { ConversationHistory } from "./ConversationHistory";
import { MessageInput } from "./MessageInput";

/**
 * Main chat interface component for Q&A mode.
 * Besluit drafting has its own dedicated page at /besluit.
 */
export function ChatInterface() {
  const { isAuthenticated, user, isLoading: authLoading, login } = useAuth();
  const { messages, isLoading, error, rateLimitCountdown, sendMessage, clearConversation } =
    useChat({ mode: "qa" });
  const {
    personas,
    currentPersonaId: currentPersona,
    needsOnboarding,
    isLoadingPreferences,
  } = useUserPreferences();

  // Track if onboarding has been dismissed this session
  const [onboardingDismissed, setOnboardingDismissed] = useState(false);

  // Get current persona details
  const personaDetails = personas.find((p) => p.personaId === currentPersona);

  // Show loading state while checking auth
  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-surface">
        <div className="text-center">
          <div className="text-6xl mb-4">🐺</div>
          <p className="text-on-surface-variant">Laden...</p>
        </div>
      </div>
    );
  }

  // Show login prompt if not authenticated
  if (!isAuthenticated) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-surface">
        <div className="text-center max-w-md mx-auto p-8">
          <div className="text-6xl mb-4">🐺</div>
          <h1 className="text-2xl font-bold text-on-surface mb-4">Welkom bij Akela</h1>
          <p className="text-on-surface-variant mb-6">
            Log in met je Google Workspace account om te beginnen.
          </p>
          <button
            onClick={login}
            className="inline-block px-6 py-3 bg-primary text-on-primary rounded-md hover:brightness-110 transition shadow-1"
          >
            Inloggen met Google
          </button>
        </div>
      </div>
    );
  }

  // Show onboarding for first-time users
  const showOnboarding =
    isAuthenticated && needsOnboarding && !onboardingDismissed && !isLoadingPreferences;

  return (
    <div className="flex flex-col h-screen">
      {/* Onboarding modal for first-time users */}
      {showOnboarding && <PersonaOnboarding onComplete={() => setOnboardingDismissed(true)} />}

      {/* Header */}
      <header className="bg-surface-container-lowest border-b border-outline-variant shadow-1">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {/* Show persona avatar instead of default wolf */}
            {personaDetails ? (
              <PersonaAvatar
                persona={personaDetails}
                size="md"
                className="border-2 border-primary-container"
              />
            ) : (
              <div className="text-4xl">🐺</div>
            )}
            <div>
              <h1 className="text-xl font-bold text-on-surface">
                {personaDetails ? personaDetails.name : "Akela"}
              </h1>
              <p className="text-sm text-on-surface-variant">
                {personaDetails?.description ?? "RAG voor Scoutinggroep de Bevers"}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-4">
            {messages.length > 0 && (
              <button
                onClick={clearConversation}
                className="text-sm text-on-surface-variant hover:text-on-surface"
              >
                Nieuw gesprek
              </button>
            )}

            {user && (
              <Link
                to="/profile"
                className="flex items-center gap-2 hover:bg-surface-container-low rounded-md p-1 transition"
              >
                {user.picture && (
                  <img
                    src={user.picture}
                    alt={user.name}
                    className="w-8 h-8 rounded-full"
                    referrerPolicy="no-referrer"
                  />
                )}
                <div className="text-sm">
                  <div className="font-medium text-on-surface">{user.name}</div>
                  {user.isAdmin && <div className="text-xs text-primary">Admin</div>}
                </div>
              </Link>
            )}

            <a
              href="/besluit"
              className="text-sm px-4 py-2 bg-primary text-on-primary rounded-md hover:brightness-110 transition shadow-1"
            >
              📝 Besluit opstellen
            </a>

            {user?.isAdmin && (
              <a
                href="/admin"
                className="text-sm px-4 py-2 bg-surface-container text-on-surface rounded-md hover:bg-surface-container-high transition"
              >
                Admin
              </a>
            )}
          </div>
        </div>
      </header>

      {/* Conversation */}
      <ConversationHistory
        messages={messages}
        userAvatar={user?.picture}
        personaAvatar={personaDetails ? `/images/${personaDetails.image}` : undefined}
        personaName={personaDetails?.name ?? "Akela"}
        isLoading={isLoading}
        error={error}
        rateLimitCountdown={rateLimitCountdown}
      />

      {/* Input */}
      <MessageInput
        onSend={sendMessage}
        disabled={isLoading || rateLimitCountdown !== null}
        placeholder="Stel een vraag over de documenten..."
      />
    </div>
  );
}
